import { FC } from 'react';
import { CheckCircle, Clock, XCircle } from 'lucide-react';
import { Badge } from './badge';
import { cn } from '@/lib/utils';

interface ApprovalStatusBadgeProps {
  status?: 'pending' | 'approved' | 'rejected';
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

export const ApprovalStatusBadge: FC<ApprovalStatusBadgeProps> = ({
  status = 'pending',
  size = 'md',
  showIcon = true
}) => {
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'approved':
        return 'default';
      case 'rejected':
        return 'destructive';
      case 'pending':
      default:
        return 'secondary';
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'approved':
        return {
          icon: CheckCircle,
          label: 'Approved'
        };
      case 'rejected':
        return {
          icon: XCircle,
          label: 'Rejected'
        };
      case 'pending':
      default:
        return {
          icon: Clock,
          label: 'Pending Approval'
        };
    }
  };

  const getSizeClasses = (size: string) => {
    switch (size) {
      case 'sm':
        return {
          container: 'px-2 py-1 text-xs',
          icon: 'w-3 h-3'
        };
      case 'lg':
        return {
          container: 'px-4 py-2 text-sm',
          icon: 'w-5 h-5'
        };
      case 'md':
      default:
        return {
          container: 'px-3 py-1 text-xs',
          icon: 'w-4 h-4'
        };
    }
  };

  const config = getStatusConfig(status);
  const sizeClasses = getSizeClasses(size);
  const Icon = config.icon;

  return (
    <Badge
      variant={getStatusVariant(status) as any}
      className={cn("inline-flex items-center gap-1.5", sizeClasses.container)}
    >
      {showIcon && <Icon className={sizeClasses.icon} />}
      {config.label}
    </Badge>
  );
};

interface ApprovalStatusDescriptionProps {
  status?: 'pending' | 'approved' | 'rejected';
  approvalDate?: string;
  approvedBy?: string;
  approvalNotes?: string;
}

export const ApprovalStatusDescription: FC<ApprovalStatusDescriptionProps> = ({
  status = 'pending',
  approvalDate,
  approvedBy,
  approvalNotes
}) => {
  const getDescription = () => {
    switch (status) {
      case 'approved':
        return {
          message: 'This customer has been approved for sales activities.',
          details: approvalDate ? `Approved on ${new Date(approvalDate).toLocaleDateString()}` : null,
          approver: approvedBy ? `by ${approvedBy}` : null
        };
      case 'rejected':
        return {
          message: 'This customer has been rejected and cannot be used for sales activities.',
          details: approvalDate ? `Rejected on ${new Date(approvalDate).toLocaleDateString()}` : null,
          approver: approvedBy ? `by ${approvedBy}` : null
        };
      case 'pending':
      default:
        return {
          message: 'This customer is waiting for management approval before sales activities can begin.',
          details: 'Contact your supervisor to expedite the approval process.',
          approver: null
        };
    }
  };

  const description = getDescription();

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{description.message}</p>
      {description.details && (
        <p className="text-xs text-muted-foreground">
          {description.details}
          {description.approver && ` ${description.approver}`}
        </p>
      )}
      {approvalNotes && status !== 'pending' && (
        <div className="mt-2 p-2 bg-background rounded-md">
          <p className="text-xs text-muted-foreground font-medium mb-1">Notes:</p>
          <p className="text-xs text-muted-foreground">{approvalNotes}</p>
        </div>
      )}
    </div>
  );
};